import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Shield } from "lucide-react";
import Footer from "../../components/footer/Footer";

/**
 * Shared shell for Privacy, Terms, and Cookie policy pages.
 * Content is passed as sections so each policy stays easy to update.
 */
export default function LegalDocumentPage({
  eyebrow,
  title,
  summary,
  lastUpdated,
  sections,
  relatedLinks = [],
}) {
  return (
    <div className="min-h-screen bg-[#f7f4ec]">
      <section className="relative overflow-hidden px-4 pb-12 pt-28 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#FFD221]/25 blur-3xl" />
          <div className="absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-white/80 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-3xl">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-black"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00]">
              <Shield size={13} />
              {eyebrow}
            </p>
            <h1 className="text-4xl font-black tracking-tight text-gray-950 sm:text-5xl">
              {title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
              {summary}
            </p>
            {lastUpdated ? (
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                Last updated · {lastUpdated}
              </p>
            ) : null}
          </motion.div>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {sections.map((section, index) => (
            <motion.article
              key={section.heading}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.2) }}
              className="rounded-3xl border border-black/[0.06] bg-white p-6 shadow-sm sm:p-8"
            >
              <h2 className="text-xl font-black text-gray-950 sm:text-2xl">
                {section.heading}
              </h2>
              {section.paragraphs?.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 48)}
                  className="mt-4 text-sm leading-7 text-gray-600 sm:text-[0.95rem]"
                >
                  {paragraph}
                </p>
              ))}
              {section.bullets?.length ? (
                <ul className="mt-4 space-y-2.5">
                  {section.bullets.map((item) => (
                    <li
                      key={item.slice(0, 48)}
                      className="flex gap-3 text-sm leading-6 text-gray-600"
                    >
                      <span
                        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FFD221]"
                        aria-hidden
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </motion.article>
          ))}

          {relatedLinks.length ? (
            <div className="rounded-3xl border border-black/[0.06] bg-white p-6 sm:p-8">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#c9a000]">
                Related policies
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {relatedLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="rounded-full border border-black/10 bg-[#f7f4ec] px-4 py-2 text-sm font-semibold text-gray-800 transition hover:border-black/20 hover:bg-[#FFD221]/35"
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  className="rounded-full border border-black/10 bg-[#f7f4ec] px-4 py-2 text-sm font-semibold text-gray-800 transition hover:border-black/20 hover:bg-[#FFD221]/35"
                >
                  Contact us
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
}
